// Maths for the host-controlled team draft.
//
// One player at a time swings between the two team zones until the host locks
// them in. Position is expressed as a signed fraction of the travel envelope:
//   -1 = fully over Vikings (left)   +1 = fully over Panthers (right)
// The component turns that into a transform; everything here is pure so the
// timing can be reasoned about (and tested) without a DOM.

// ── The waiting swing ────────────────────────────────────────────────────────
// The card oscillates forever while the host builds the moment. The period
// tightens the longer they wait, so a long hold feels like mounting pressure
// rather than a metronome.
export const SWING_PERIOD_MS = 1150;      // one full left → right → left cycle
export const SWING_MIN_PERIOD_MS = 620;   // fastest it ever gets
export const TENSION_RAMP_MS = 9000;      // time taken to reach that top speed

export function swingPeriodAt(heldMs) {
  const t = Math.min(1, Math.max(0, (heldMs || 0) / TENSION_RAMP_MS));
  return SWING_PERIOD_MS + (SWING_MIN_PERIOD_MS - SWING_PERIOD_MS) * t;
}

// Position for a given phase angle. The loop accumulates phase rather than
// deriving it from elapsed time, because the period changes as tension builds
// and recomputing sin(2πt/T) with a moving T would make the card jump.
export function swingOffset(phase) {
  return Math.sin(phase);
}

// Phase advance for one frame at the current period.
export function phaseStep(deltaMs, periodMs) {
  return (deltaMs / periodMs) * Math.PI * 2;
}

// ── The finale ───────────────────────────────────────────────────────────────
// Triggered when the host locks in. It deliberately settles on the WRONG side
// first — that near-miss is the whole point of the ceremony — before slamming
// across to the team the player was actually dealt.
export const FINALE = {
  approach: 340,  // glide from wherever the swing was, over to the wrong side
  hang: 220,      // hover there, quivering, as if it has decided
  slam: 380,      // wind up, then rocket across to the real team
  settle: 420,    // shake and confetti before the next player drops in
};

export const FINALE_LANDED_MS = FINALE.approach + FINALE.hang + FINALE.slam;
export const FINALE_TOTAL_MS = FINALE_LANDED_MS + FINALE.settle;

// Fraction of the slam spent winding up deeper onto the wrong side before the
// launch, and how far past the resting point that wind-up reaches.
const WIND_UP_FRACTION = 0.22;
const WIND_UP_DEPTH = 0.07;

export const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
export const easeInOutCubic = (t) =>
  (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

// Where the card sits `elapsed` ms into the finale.
//   fromX      — position when the host locked in, so the glide starts smoothly
//   targetSign — -1 for Vikings, +1 for Panthers
// Returns { x, stage } with stage in 'approach' | 'hang' | 'slam' | 'landed'.
export function finalePosition(elapsed, fromX, targetSign) {
  const wrong = -targetSign * 0.92;   // near-miss resting point
  const target = targetSign;
  const { approach, hang, slam } = FINALE;

  if (elapsed < approach) {
    const t = easeOutCubic(elapsed / approach);
    return { x: fromX + (wrong - fromX) * t, stage: 'approach' };
  }
  if (elapsed < approach + hang) {
    // A dead-still hold. It used to quiver here, but a vibrating name is hard
    // to read and looks like a glitch rather than suspense — the stillness
    // after the glide does the work on its own.
    return { x: wrong, stage: 'hang' };
  }
  const t = Math.min(1, (elapsed - approach - hang) / slam);

  // Anticipation: a brief wind-up deeper onto the wrong side. It sells the
  // fake-out, and it keeps the launch from reading as a teleport — an easeOut
  // here would put nearly half the travel into the first two frames.
  const windTo = wrong - targetSign * WIND_UP_DEPTH;
  if (t < WIND_UP_FRACTION) {
    return { x: wrong + (windTo - wrong) * easeOutCubic(t / WIND_UP_FRACTION), stage: 'slam' };
  }

  // Launch: eases out of the wind-up, quick across the middle, hard arrival.
  const u = (t - WIND_UP_FRACTION) / (1 - WIND_UP_FRACTION);
  return {
    x: windTo + (target - windTo) * easeInOutCubic(u),
    stage: u < 1 ? 'slam' : 'landed',
  };
}

// Which zone a position is over. Exactly 0 counts as Vikings so the return is
// never undefined.
export function sideOf(x) {
  return x < 0 ? 'vikings' : 'panthers';
}

export function signOf(teamId) {
  return teamId === 'vikings' ? -1 : 1;
}
